import React from 'react';
import {
  humanizeEntityRef,
  EntityRefLink,
  catalogApiRef,
  EntityDisplayName,
} from '@backstage/plugin-catalog-react';
import { Chip } from '@material-ui/core';
import { CatalogTableRow } from './types';
import { OverflowTooltip, TableColumn } from '@backstage/core-components';
import {
  Entity,
  EntityRelation,
  parseEntityRef,
} from '@backstage/catalog-model';
import { JsonArray } from '@backstage/types';
import { CustomEntityRelation } from './CatalogTable';
import { useAsync } from 'react-use';
import { useApi } from '@backstage/core-plugin-api';

type ReferenceLinkProps = {
  entity: Entity;
};

type GroupReferenceLinkProps = {
  domainEntityRelation: EntityRelation;
  groupEntity: Entity;
};

export const columnFactories = Object.freeze({
  createNameColumn(options?: {
    defaultKind?: string;
  }): TableColumn<CatalogTableRow> {
    function formatContent(entity: Entity): string {
      return (
        entity.metadata?.title ||
        humanizeEntityRef(entity, {
          defaultKind: options?.defaultKind,
        })
      );
    }

    function GroupReferenceLink(props: GroupReferenceLinkProps): JSX.Element {
      const catalogApi = useApi(catalogApiRef);
      const { domainEntityRelation, groupEntity } = props;
      const { value: domainEntity, loading } = useAsync(() =>
        catalogApi.getEntityByRef(
          parseEntityRef(domainEntityRelation?.targetRef),
        ),
      );

      if (loading) {
        return <div>Load data...</div>;
      }

      return (
        <EntityRefLink entityRef={domainEntity as Entity}>
          <EntityDisplayName entityRef={groupEntity} />
        </EntityRefLink>
      );
    }

    function ReferenceLink(props: ReferenceLinkProps): JSX.Element {
      const { kind, relations } = props.entity;
      const domainOwner: EntityRelation | undefined = relations?.find(
        (v: CustomEntityRelation) => v?.target?.kind === 'domain',
      );

      if (domainOwner && kind === 'Group') {
        return (
          <GroupReferenceLink
            domainEntityRelation={domainOwner}
            groupEntity={props.entity}
          />
        );
      }

      return (
        <EntityRefLink
          entityRef={props.entity}
          defaultKind={options?.defaultKind || 'Component'}
        />
      );
    }

    return {
      title: 'Name',
      field: 'resolved.entityRef',
      highlight: true,
      width: '40%',
      cellStyle: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        width: '10%',
      },
      headerStyle: {
        width: '40%',
      },
      customSort({ entity: entity1 }, { entity: entity2 }) {
        // TODO: We could implement this more efficiently by comparing field by field.
        // This has similar issues as above.
        return formatContent(entity1).localeCompare(formatContent(entity2));
      },
      render: ({ entity }) => <ReferenceLink entity={entity} />,
    };
  },

  createSystemColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Product',
      field: 'resolved.partOfSystemRelationTitle',
      render: ({ resolved }) => {
        if (
          resolved?.product &&
          resolved?.product?.length > 0 &&
          Array.isArray(resolved?.product)
        ) {
          const product = resolved?.product as string[];

          return product.map(item => {
            const title = resolved?.productList?.find(
              x => x?.metadata?.name === item,
            )!!;
            return (
              <EntityRefLink
                entityRef={{
                  name: title?.metadata?.name,
                  namespace: 'default',
                  kind: 'system',
                }}
              />
            );
          });
        } else {
          const title = resolved?.productList?.find(
            x => x?.metadata?.name === resolved.partOfSystemRelationTitle,
          )!!;
          return title ? (
            <EntityRefLink
              entityRef={{
                name: title?.metadata?.name,
                namespace: 'default',
                kind: 'system',
              }}
            />
          ) : null;
        }
      },
    };
  },

  createProductColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Product',
      field: 'entity.spec.products',
      render: (row:any) => {
        const products = row?.metadata?.name;
        if(products){
          return (
          <EntityRefLink
            entityRef={{
              name: products,
              namespace: 'default',
              kind: 'system',
            }}
          />
        );
        }
        return null;
      },
    };
  },
  createContentPageNameColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Page name',
      render: ({ resolved }) => {
        return (
          <b>
            <EntityRefLink entityRef={resolved.entityRef} />
          </b>
        );
      },
    };
  },
  createContentPageTopicColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Topic',
      render: ({ resolved }) => {
        return <p>{resolved.topic}</p>;
      },
    };
  },
  createContentPageContentManagerColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Content Manager',
      render: ({ resolved }) => {
        return resolved?.manager ? (
          <EntityRefLink
            entityRef={resolved.manager?.link?.toLowerCase() as string}
          />
        ) : (
          <></>
        );
      },
    };
  },
  createBusinessComponentColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Owner',
      render: ({ resolved }) => {
        return resolved?.manager ? (
          <EntityRefLink
            entityRef={resolved.manager?.link?.toLowerCase() as string}
          />
        ) : (
          <></>
        );
      },
    };
  },
  createContactPersonColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Contact Person',
      render: ({ resolved }) => {
        return resolved?.manager ? (
          <EntityRefLink
            entityRef={resolved.manager?.link?.toLowerCase() as string}
          />
        ) : (
          <></>
        );
      },
    };
  },
  createOwnerColumn(filter?: string): TableColumn<CatalogTableRow> {
    if (filter && filter === 'domain') {
      return {};
    }

    return {
      title: 'Owner',
      field: 'resolved.ownedByRelationsTitle',
      render: ({ resolved }) => {
        const { ownedByRelations } = resolved;

        if (!ownedByRelations || ownedByRelations.length === 0) {
          return null;
        }

        const renderedNames = new Set();

        return ownedByRelations.flatMap((item, index) => {
          const { name, kind, namespace } = item;

          // Split name by space into individual parts
          const nameParts = name.split(' ');

          // Filter out already rendered names
          const uniqueParts = nameParts.filter(n => {
            if (renderedNames.has(n)) return false;
            renderedNames.add(n);
            return true;
          });

          // Return EntityRefLink for each unique part
          return uniqueParts.map((n, subIndex) => (
            <EntityRefLink
              key={`${index}-${subIndex}`}
              entityRef={{ name: n, namespace, kind }}
            />
          ));
        });
      },
    };
  },
  createSpecTargetsColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Targets',
      field: 'entity.spec.targets',
      render: ({ entity }) => (
        <>
          {(entity?.spec?.targets || entity?.spec?.target) && (
            <OverflowTooltip
              text={(
                (entity!.spec!.targets as JsonArray) || [entity.spec.target]
              ).join(', ')}
              placement="bottom-start"
            />
          )}
        </>
      ),
    };
  },
  createSpecTypeColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Type',
      field: 'entity.spec.type',
      hidden: true,
      width: 'auto',
    };
  },
  createSpecLifecycleColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Lifecycle',
      field: 'entity.spec.lifecycle',
      width: 'auto',
    };
  },
  createMetadataDescriptionColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Description',
      field: 'entity.metadata.description',
      render: ({ entity }) => entity.metadata.description,
      width: '40%',
      cellStyle: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        width: '10%',
      },
      headerStyle: {
        width: '40%',
      },
    };
  },

  createTagsColumn(filter?: string): TableColumn<CatalogTableRow> {
    return {
      ...(filter === 'domain' && { defaultSort: 'desc' }),
      title: 'Tags',
      field: 'entity.metadata.tags',
      cellStyle: {
        padding: '15px 16px 10px 20px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '5px',
      },
      render: ({ entity }) => (
        <>
          {entity.metadata.tags?.map(t => (
            <Chip
              key={t}
              label={t}
              size="small"
              variant="outlined"
              style={{ marginBottom: '0px' }}
            />
          ))}
        </>
      ),
      width: 'auto',
    };
  },
  createTitleColumn(options?: {
    hidden?: boolean;
  }): TableColumn<CatalogTableRow> {
    return {
      title: 'Title',
      field: 'entity.metadata.title',
      hidden: options?.hidden,
      searchable: true,
    };
  },
  createLabelColumn(
    key: string,
    options?: { title?: string; defaultValue?: string },
  ): TableColumn<CatalogTableRow> {
    return {
      title: options?.title || 'Label',
      field: 'entity.metadata.labels',
      cellStyle: {
        padding: '0px 16px 0px 20px',
      },
      render: ({ entity }: { entity: Entity }) => {
        const labels: Record<string, string> | undefined =
          entity.metadata?.labels;
        const specifiedLabelValue =
          (labels && labels[key]) || options?.defaultValue;
        return (
          <>
            {specifiedLabelValue && (
              <Chip
                key={specifiedLabelValue}
                label={specifiedLabelValue}
                size="small"
                variant="outlined"
              />
            )}
          </>
        );
      },
      width: 'auto',
    };
  },
  createNamespaceColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Namespace',
      field: 'entity.metadata.namespace',
      width: 'auto',
    };
  },
});
